import { createDraftSafeSelector } from '@reduxjs/toolkit'
import _ from 'lodash'
import type { RootState } from '@/redux/store'
import type { BulkEditState } from './slice'
import {
  type BulkEditValidationError,
  type Field,
  type FieldAction,
  type FieldName,
  type FieldValue,
  type Id,
  isMultiSelectValue,
  type MissingRequirement,
  type SelectOption,
} from './types'

import validators from './validation'

const selectSelf = (state: RootState): BulkEditState => state.reducer.bulkEdit

export const selectAllFields = (state: RootState): BulkEditState['fields'] =>
  state.reducer.bulkEdit.fields
export const selectAllValues = (state: RootState): BulkEditState['values'] =>
  state.reducer.bulkEdit.values
export const selectAllOptions = (state: RootState): BulkEditState['options'] =>
  state.reducer.bulkEdit.options

export const selectAllFieldActivity = (
  state: RootState,
): BulkEditState['activeFields'] => state.reducer.bulkEdit.activeFields

export const selectValueByName =
  (fieldName: FieldName) =>
  (state: RootState): FieldValue | undefined =>
    state.reducer.bulkEdit.values[fieldName]
export const selectFieldByName =
  (fieldName: FieldName) =>
  (state: RootState): Field =>
    state.reducer.bulkEdit.fields[fieldName]
export const selectOptionsByName =
  (fieldName: FieldName) =>
  (state: RootState): SelectOption[] =>
    state.reducer.bulkEdit.options[fieldName]
export const selectOptionByNameAndCode =
  (fieldName: FieldName, code: Id) =>
  (state: RootState): SelectOption | undefined =>
    state.reducer.bulkEdit.options[fieldName].find((opt) => opt.code === code)

export const selectDisabledOptionIdsByFieldName = (fieldName: FieldName) =>
  createDraftSafeSelector(
    selectAllValues,
    selectOptionsByName(fieldName),
    (allValues, fieldOptions) => {
      if (!Array.isArray(fieldOptions)) return
      const missingOptions: Id[] = []
      fieldOptions.forEach((option: SelectOption) => {
        if (!option || !option.requires) return

        for (const _reqFieldName in option.requires) {
          const reqFieldName = _reqFieldName as FieldName
          const fieldValue = allValues[reqFieldName] as SelectOption[]

          if (!fieldValue || !Array.isArray(fieldValue)) break

          const satisfiesReq = fieldValue.find((o) => {
            if (!option.requires) return false
            const optionReqs = option.requires[reqFieldName]
            if (optionReqs) return optionReqs.includes(o.code)
          })

          if (satisfiesReq) return true
        }

        missingOptions.push(option.code)
      })

      return missingOptions
    },
  )

export const selectMissingRequirements = () =>
  createDraftSafeSelector(
    selectAllFields,
    selectAllValues,
    selectAllOptions,
    (allFields, allValues, allOptions) => {
      const missingRequirements: MissingRequirement[] = []

      // for each field with a value that is multiselect
      let fieldName: FieldName
      for (fieldName in allFields) {
        const field = allFields[fieldName]
        if (field.inputType !== 'multiselect') continue //skip non-multiselect
        const fieldValues = allValues[fieldName] as SelectOption[]
        if (!fieldValues) continue
        for (const option of fieldValues) {
          if (!option.requires) continue //skip no reqs

          for (const _requiredFieldName in option.requires) {
            const requiredFieldName = _requiredFieldName as FieldName
            const valuesToCheck = option.requires[requiredFieldName] as Id[]
            const missingField = allFields[requiredFieldName]
            const reqFieldValues = allValues[
              requiredFieldName
            ] as SelectOption[]
            const reqFieldOptions = allOptions[requiredFieldName]

            if (!reqFieldValues || !reqFieldOptions) continue //skip no required values
            const reqFieldValueIds = reqFieldValues.map(
              (opt: SelectOption) => opt.code,
            )
            const missingIds = _.differenceWith(valuesToCheck, reqFieldValueIds)
            if (missingIds.length > 0) {
              const missingOptions = reqFieldOptions.filter((opt) =>
                missingIds.includes(opt.code),
              )

              missingRequirements.push({
                option,
                fieldName,
                fieldLabel: field.label,
                missingField,
                missingOptions,
              })
            }
          }
        }
      }

      return missingRequirements
    },
  )

export const selectField = (fieldName: FieldName) =>
  createDraftSafeSelector(selectSelf, (state) => ({
    field: state.fields[fieldName],
    value: state.values[fieldName],
    options: state.options[fieldName],
    isOpen: state.openFields[fieldName],
    isActive: state.activeFields[fieldName],
    fieldAction: state.fieldAction[fieldName],
  }))

export const selectValidationOfActiveValues = () =>
  createDraftSafeSelector(
    selectActiveFields(),
    selectMissingRequirements(),
    selectActiveFieldsByActionType(null),
    (
      activeFields,
      missingReqs,
      activeFieldsMissingAction,
    ): [boolean, TODO[]] => {
      const errors: BulkEditValidationError[] = []

      for (const [field, value] of activeFields) {
        const validateTests = validators[field.name]
        if (validateTests === undefined) continue
        validateTests.map((func) => {
          try {
            func.validateSync(value)
            1
          } catch (err) {
            if (typeof err === 'object' && err !== null && 'errors' in err)
              errors.push({
                field: field.label,
                errorMessages: err.errors as string[],
              })
          }
        })
      }

      missingReqs.forEach((req: MissingRequirement) => {
        if (
          activeFields.find(([field]: any[]) => field.name === req.fieldName)
        ) {
          errors.push({
            field: req.fieldLabel,
            errorMessages: [
              `Requires ${req.missingField.label} to contain ${req.missingOptions.map((o: SelectOption) => o.description)}`,
            ],
          })
        }
      })

      activeFieldsMissingAction.forEach(([field]) => {
        errors.push({
          field: field.label,
          errorMessages: [
            `A field action is required for ${field.label}. Select Append or Replace.`,
          ],
        })
      })

      return [!errors.length, errors]
    },
  )

export const selectActiveFields = () =>
  createDraftSafeSelector(
    selectSelf,
    (state: RootState) => state.reducer.media.isPerkins,
    (state, isPerkins): [Field, FieldValue][] => {
      const platform = isPerkins ? 'perkins' : 'cat'

      const isSamePlatform = (field: Field) => {
        if (!field.platform) return true
        return field.platform === platform
      }

      return Object.entries(state.values)
        .filter(([fieldName]) => state.activeFields[fieldName as FieldName])
        .map(([fieldName, value]): [Field, FieldValue] => [
          state.fields[fieldName as FieldName],
          value,
        ])
        .filter(([field]) => isSamePlatform(field as Field))
    },
  )

export const selectActiveFieldsByActionType = (actionType: FieldAction) =>
  createDraftSafeSelector(
    selectSelf,
    selectActiveFields(),
    (state, activeFields) => {
      return activeFields.filter(([field, value]) => {
        let fieldAction = state.fieldAction[field.name]
        if (fieldAction === undefined) fieldAction = 'replace'
        // null is used for unselected values

        const isValueEmpty = Array.isArray(value) && value?.length === 0

        if (fieldAction === 'append' && isValueEmpty) {
          return false // appended empty arrays are no-op so filter them out
        }

        return fieldAction === actionType
      })
    },
  )

export const selectActiveValuesForSubmission = () =>
  createDraftSafeSelector(
    selectActiveFields(),
    selectSelf,
    (activeFields, state) => {
      const fields = activeFields.map(([field, values]) => {
        //return non-array values
        if (!Array.isArray(values)) {
          if (typeof values === 'object' && values?.hasOwnProperty('code')) {
            const { code } = values
            return [field.name, code] as [FieldName, FieldValue]
          } else {
            return [field.name, values] as [FieldName, FieldValue]
          }
        }

        //return codes from multiselect objects
        if (isMultiSelectValue(values))
          if (values.find((v) => v?.code)) {
            return [field.name, values.map((v) => v?.code)]
          }

        return [field.name, values] as [FieldName, FieldValue]
      })

      const response: Partial<Record<FieldName, any>> = {}

      for (const f of fields) {
        const [fieldName, value] = f as [FieldName, any]
        if (Array.isArray(value)) {
          if (state.fieldAction[fieldName] === 'append' && value.length === 0)
            continue
          response[fieldName] = {
            update: true,
            values: value,
            append: state.fieldAction[fieldName] === 'append',
          }
        } else {
          response[fieldName] = { update: true, value }
        }
      }

      return response
    },
  )
